/**
 * Conversion tracking — fires to GA4 (all platforms) plus ad pixels (web only).
 *
 * GA4 events work on both web and native (via Capacitor Firebase Analytics),
 * so conversions from native app users are captured for GA4-based Google Ads
 * conversion imports. The gtag/rdt pixels are web-only extras.
 */

import { trackCriticalEvent, trackEvent } from './analyticsTracker';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    rdt?: (...args: any[]) => void;
  }
}

/** Google Ads conversion IDs (web-only gtag pixels).
 *
 *  The website "Sign-up" pixel (`AW-18099371422/ye-sCMTSw58cEJ77ubZD`) was
 *  retired 2026-09-16: it only ever fired on my.enough.app, duplicated the GA4
 *  `conversion_signup` import, and read 0 in Ads because ~all paid traffic is
 *  the Android app. Web signups are counted through the GA4 import alone. */
const GOOGLE_ADS_CONVERSIONS = {
  paidConversion: 'AW-18099371422/0AqxCKDq158cEJ77ubZD',
};

/** Unit weight carried on `conversion_signup`. A signup has no revenue; the
 *  value is a count-weight so the GA4→Ads import has a `value`/`currency` pair
 *  to model on. Whether Ads actually uses it is decided by the conversion
 *  action's value setting in the Ads UI, not here. */
const SIGNUP_CONVERSION_VALUE_USD = 1;

/** Reddit Ads conversion event names */
const REDDIT_CONVERSIONS = {
  signup: 'SignUp',
  paidConversion: 'Purchase',
};

function isProduction(): boolean {
  try {
    return import.meta.env.VITE_APP_ENV === 'production';
  } catch {
    return false;
  }
}

function gtagConversion(sendTo: string, value?: number) {
  if (!isProduction() || !sendTo || !window.gtag) return;
  window.gtag('event', 'conversion', {
    send_to: sendTo,
    ...(value ? { value, currency: 'USD' } : {}),
  });
}

function redditConversion(eventName: string) {
  if (!isProduction() || !window.rdt) return;
  window.rdt('track', eventName);
}

/** Track a new user signup across all platforms.
 *
 *  `conversion_signup` is the Google Ads conversion for the Android App
 *  campaign (imported from GA4/Firebase). Since 2026-09-16 it carries:
 *   - `transaction_id` = `signup:<uid>` — stable per account (link-in-place
 *     keeps the anonymous uid; a fresh account gets its new uid), so Ads can
 *     dedupe a double fire and the id matches the offline feed's orderId
 *     (`marketing/offline_conversions.py`).
 *   - `value` / `currency` — the unit weight above.
 *  Omitting the uid still fires the event (never drop a conversion for a
 *  missing id); it just goes without a transaction_id.
 *
 *  Resolves once the GA4 transport has accepted the event (native: the bridge
 *  round-trip), bounded by trackCriticalEvent's timeout, so the signup path can
 *  await it before the WebView can be backgrounded or closed. */
export function trackSignupConversion(params?: { uid?: string }): Promise<void> {
  const uid = params?.uid?.trim();
  // GA4 event — works on web + native, importable as Google Ads conversion
  const delivered = trackCriticalEvent('conversion_signup', {
    ...(uid ? { transaction_id: `signup:${uid}` } : {}),
    value: SIGNUP_CONVERSION_VALUE_USD,
    currency: 'USD',
  });
  // Web-only ad pixel (Reddit). The Google Ads website Sign-up pixel is
  // retired — see GOOGLE_ADS_CONVERSIONS.
  redditConversion(REDDIT_CONVERSIONS.signup);
  return delivered;
}

/** Track a paid subscription conversion across all platforms.
 *  Fire when money actually moves — a direct paid start or a trial
 *  converting — never at trial start (see trackTrialStartConversion).
 *
 *  Carry the real dollar value: without it the Google Ads GA4 import counts
 *  every paid conversion at the $1 default (verified in the Ads console
 *  2026-07-21), which poisons value-based bidding the moment it's enabled. */
export function trackPaidConversion(params?: { value?: number; uid?: string }): Promise<void> {
  const uid = params?.uid?.trim();
  // GA4 event — works on web + native. Critical delivery (2026-09-17): this
  // fires as the user returns from the store sheet, the moment the WebView is
  // most likely to be suspended — see trackTrialStartConversion.
  const delivered = trackCriticalEvent('conversion_paid', {
    ...(uid ? { transaction_id: `paid:${uid}` } : {}),
    ...(params?.value ? { currency: 'USD', value: params.value } : {}),
  });
  // Web-only ad pixels. Wrapped: these call third-party globals (gtag, rdt)
  // and this function is now awaited inside purchase-success handlers, so a
  // throw here would surface as a failed purchase.
  try {
    gtagConversion(GOOGLE_ADS_CONVERSIONS.paidConversion, params?.value);
    redditConversion(REDDIT_CONVERSIONS.paidConversion);
  } catch {
    // a pixel is never worth failing the caller
  }
  return delivered;
}

const TRIAL_TRACKED_KEY_PREFIX = 'enough_trial_start_tracked:';

function markTrialStartTracked(uid: string) {
  try {
    localStorage.setItem(`${TRIAL_TRACKED_KEY_PREFIX}${uid}`, '1');
  } catch {
    // storage unavailable — the Ads transaction_id still dedupes
  }
}

/** Whether this device already fired `conversion_trial_started` for `uid`.
 *  Lets the stranded-purchase resume (AuthGuard) re-fire only when the point
 *  of sale never got to — without inflating GA4 on every relaunch. */
export function wasTrialStartTracked(uid: string): boolean {
  try {
    return localStorage.getItem(`${TRIAL_TRACKED_KEY_PREFIX}${uid}`) === '1';
  } catch {
    return false;
  }
}

/** Track a free-trial start across all platforms.
 *
 *  Deliberately GA4-only (no AW pixel): import `conversion_trial_started` in
 *  Google Ads as its OWN conversion action (secondary, or primary while the
 *  trial cohort matures) so bidding gets a fast signal without conflating
 *  trial-starts with revenue. The `conversion_paid` action stays reserved for
 *  real payment events. `value` is accepted for a paid-from-day-one tier but
 *  the point-of-sale caller never sends it for a free trial.
 *
 *  Critical delivery (2026-09-17): this is the single event most exposed to
 *  the WebView-lifecycle loss that `trackCriticalEvent` exists for. It fires
 *  the instant the user returns from the App Store / Play purchase sheet —
 *  i.e. the app has just been backgrounded and is maximally likely to be
 *  suspended or killed — so on plain `trackEvent` it could sit in the JS
 *  pendingOps queue and die with the WebView before the native SDK ever saw
 *  it. `conversion_signup` was moved to critical delivery on 2026-09-16
 *  (PR #534); this is the same fix for the event Google Ads actually bids on.
 *  Internal counts have run far ahead of Ads' (334 vs 84 on 2026-09-08).
 *
 *  `transaction_id` = `trial:<uid>` gives Ads a dedupe key, matching the
 *  `signup:<uid>` shape. The uid may be the ANONYMOUS one — the carousel
 *  sells before sign-in — which is fine and stable: link-in-place keeps it. */
export function trackTrialStartConversion(
  params?: { tier?: string; value?: number; uid?: string },
): Promise<void> {
  const uid = params?.uid?.trim();
  if (uid) markTrialStartTracked(uid);
  return trackCriticalEvent('conversion_trial_started', {
    ...(uid ? { transaction_id: `trial:${uid}` } : {}),
    ...(params?.tier ? { tier: params.tier } : {}),
    ...(params?.value ? { currency: 'USD', value: params.value } : {}),
  });
}

/** Track a completed NEW subscription purchase at the point of sale.
 *
 *  Point-of-sale ownership (2026-07-30): the launch funnel sells on an
 *  ANONYMOUS uid before sign-in (value_onboarding carousel, screen 5), and
 *  SubscriptionContext's entitlement observer can never see that purchase —
 *  entitlements only hydrate after signup, and the observer's first hydration
 *  deliberately seeds its refs without firing (so existing members aren't
 *  recounted on every launch). Net effect: ZERO trial-start events for the
 *  whole native funnel (verified in GA, 2026-07-30, 8 real trials / 0 events).
 *
 *  So the purchase-success paths in SpendingToolsPaywall own ALL new-purchase
 *  conversion events via this function — it works in anonymous sessions too
 *  (GA4 keys on the app instance, not auth). The observer keeps only what a
 *  point of sale can't see: the day-30 trial→paid flip and promotional grants.
 */
export function trackSubscriptionPurchase(params: {
  tier: string;
  isTrial: boolean;
  /** Plan price. Only attached to the conversion when money actually changed
   *  hands (`isTrial === false`) — a $0 trial must not carry the list price. */
  value?: number;
  uid?: string;
}): Promise<void> {
  trackEvent('subscription_started', { tier: params.tier, is_trial: params.isTrial });
  if (params.isTrial) {
    // No value on a trial start. Callers pass the plan price for the paid
    // branch below; forwarding it here stamped $15 on every $0 trial and
    // inflated Google Ads conversion value ~36x (audit 2026-09-08).
    // Returned so the purchase-success handler can await delivery before the
    // WebView can be torn down (2026-09-17).
    return trackTrialStartConversion({ tier: params.tier, uid: params.uid });
  } else {
    trackEvent('purchase', {
      ...(params.value ? { currency: 'USD', value: params.value } : {}),
      transaction_id: `${params.uid ?? 'anon'}_${Date.now()}`,
      item_name: params.tier,
      item_category: 'subscription',
    });
    return trackPaidConversion({ value: params.value, uid: params.uid });
  }
}
